import React, { createContext, useState, useContext, useEffect } from 'react';
import { api, attempt, uploadImage } from '../lib/api';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activeDomain, setActiveDomain] = useState(null);
  const [products, setProducts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  // Admin States
  const [allUsers, setAllUsers] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [allDevices, setAllDevices] = useState([]);

  const clearUser = () => {
    setUser(null);
    setProfile(null);
    setActiveDomain(null);
    setDevices([]);
    setOrders([]);
    setAllUsers([]);
    setAllOrders([]);
    setAllDevices([]);
  };

  const fetchProducts = async () => {
    const { data } = await attempt(api.get('products'));
    if (data) setProducts(data.products);
  };

  const fetchAdminData = async () => {
    const { data } = await attempt(api.get('admin/data'));
    if (data) {
      setAllUsers(data.users);
      setAllOrders(data.orders);
      setAllDevices(data.devices);
    }
  };

  // Loads everything for the signed-in user. Returns false if there is no session.
  const fetchUserData = async () => {
    try {
      const data = await api.get('me');
      setUser(data.user);
      setProfile(data.profile);
      setActiveDomain(data.activeDomain);
      setDevices(data.devices);
      setOrders(data.orders);
      if (data.profile?.is_admin) await fetchAdminData();
      return true;
    } catch (err) {
      if (err.status === 401) clearUser();
      else console.error('Error fetching user data:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchUserData().finally(() => setLoading(false));
    fetchProducts();
  }, []);

  const updateProfile = async (data, targetUserId = null) => {
    const userId = targetUserId || user?.id;
    if (!userId) return { error: { message: 'No user found' } };

    const { data: result, error } = await attempt(api.patch(`profiles/${encodeURIComponent(userId)}`, data));
    if (!error) {
      if (userId === user?.id) setProfile(result.profile);
      if (profile?.is_admin) await fetchAdminData();
    }
    return { error };
  };

  const updateAuth = async (data) => {
    const { email, password } = data;
    const { data: result, error } = await attempt(api.post('auth/update', { email, password }));
    if (!error && email) setUser(result.user);
    return { error };
  };

  const linkDevice = async (serial, name) => {
    if (!user) return { error: 'Not logged in' };
    const { error } = await attempt(api.post('devices/link', { serial, name }));
    if (error) return { error: error.message };
    await fetchUserData();
    return { error: null };
  };

  const claimFreeDevice = async (productId) => {
    const { data, error } = await attempt(api.post('devices/claim-free', { productId }));
    if (error) return { success: false, error };
    setOrders(prev => [data.order, ...prev]);
    setProfile(data.profile);
    return { success: true, error: null };
  };

  const unlinkDevice = async (deviceId) => {
    const { error } = await attempt(api.post(`devices/${deviceId}/unlink`));
    if (!error) await fetchUserData();
    return { error };
  };

  const preloadDevice = async (serial, type, name) => {
    const { error } = await attempt(api.post('admin/devices', { serial, type, name }));
    if (!error) await fetchAdminData();
    return { error };
  };

  const adminAssignDevice = async (deviceId, userId) => {
    const { error } = await attempt(api.post(`admin/devices/${deviceId}/assign`, { userId }));
    if (!error) await fetchAdminData();
    return { error };
  };

  const adminDeassignDevice = async (deviceId) => {
    const { error } = await attempt(api.post(`admin/devices/${deviceId}/deassign`));
    if (!error) await fetchAdminData();
    return { error };
  };

  const deleteDeviceMaster = async (deviceId) => {
    const { error } = await attempt(api.del(`admin/devices/${deviceId}`));
    if (!error) await fetchAdminData();
    return { error };
  };

  // Product lists are updated from the response rather than re-fetched,
  // because NCB reads can briefly lag behind a write.
  const createProduct = async (data) => {
    const { data: result, error } = await attempt(api.post('admin/products', data));
    if (!error) setProducts(prev => [result.product, ...prev]);
    return { error };
  };

  const updateProduct = async (id, data) => {
    const { data: result, error } = await attempt(api.patch(`admin/products/${id}`, data));
    if (!error) setProducts(prev => prev.map(p => (p.id === String(id) ? result.product : p)));
    return { error };
  };

  const deleteProduct = async (id) => {
    const { error } = await attempt(api.del(`admin/products/${id}`));
    if (!error) setProducts(prev => prev.filter(p => p.id !== String(id)));
    return { error };
  };

  // The path argument is kept for existing callers; the server decides the
  // folder from the signed-in user.
  const uploadFile = async (bucket, file) => {
    try {
      return { url: await uploadImage(bucket, file) };
    } catch (err) {
      return { error: { message: err.message } };
    }
  };

  const uploadProductImage = async (file) => {
    return uploadFile('product-images', file);
  };

  // Login and sign-up return { data, error } like the old Supabase calls.
  const login = async (e, p) => {
    const result = await attempt(api.post('auth/login', { email: e, password: p }));
    if (!result.error) await fetchUserData();
    return result;
  };

  const logout = async () => {
    await attempt(api.post('auth/logout'));
    clearUser();
  };

  const signUp = async (e, p, m) => {
    const result = await attempt(api.post('auth/signup', {
      email: e,
      password: p,
      firstName: m.firstName,
      lastName: m.lastName
    }));
    if (!result.error) await fetchUserData();
    return result;
  };

  const addToCart = (p, o) => setCart(prev => [...prev, { ...p, cartId: Date.now(), options: o }]);
  const removeFromCart = (id) => setCart(prev => prev.filter(i => i.cartId !== id));

  const checkout = async () => {
    const { data, error } = await attempt(api.post('orders', { items: cart }));
    if (!error) {
      setOrders(prev => [data.order, ...prev]);
      setCart([]);
      return true;
    }
    return false;
  };

  return (
    <AppContext.Provider value={{
      user, login, signUp, logout, profile, updateProfile, updateAuth, activeDomain,
      products, createProduct, updateProduct, deleteProduct, uploadProductImage,
      devices, linkDevice, unlinkDevice, claimFreeDevice, preloadDevice, adminAssignDevice, adminDeassignDevice, deleteDeviceMaster,
      cart, addToCart, removeFromCart, checkout, orders, loading,
      allUsers, allOrders, allDevices, uploadFile
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
